import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useBaseAction, useHandleModals } from "hooks/core";
import { API } from "constants/RestApi";
import {
  IAvailableFeatureData,
  ISharedConfig,
  SharedConfigFormData,
} from "components/features/types";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tab } from "react-bootstrap";
import DataTable from "components/common/DataTable/DataTable";
import { useSharedConfigsColumns } from "components/features/columns/sharedConfigsColumns";
import CreateEditSharedConfigModal from "components/features/CreateEditSharedConfigModal";
import WarningModal from "components/common/Modals/WarningModal";
import generateTemplateFromSchema from "core/utils/SchemaConverting";
import { JsonEditor } from "json-edit-react";
import useAvailableFeaturesManagement from "hooks/tenant/useAvailableFeaturesManagement";
import TabItem from "components/common/Tab/TabItem";
import PageTitle from "../../../../components/common/Typography/PageTitle";

const actions = ["addEditConfig", "deleteConfig", "toggleEnabled"] as const;

const FeatureDetailsScreen = () => {
  const { featureId } = useParams<{ featureId: string }>();
  const { request } = useBaseAction();
  const [feature, setFeature] = useState<IAvailableFeatureData>();
  const [activeTab, setActiveTab] = useState("details");
  const [sharedConfigs, setSharedConfigs] = useState<ISharedConfig[]>([]);
  const [deleteEntity, setDeleteEntity] = useState<{ id: string; rowVersion: string }>();

  const { availableFeatures } = useAvailableFeaturesManagement({
    fetchOnLoad: false,
  });

  const { toggleModal, modals, selectedEntity } = useHandleModals<
    ISharedConfig,
    typeof actions[number]
  >({
    modalsKeys: actions,
  });

  const fetchSharedConfigs = async () => {
    const params = {
      filter: `featureId/value eq '${featureId}'`,
    };
    try {
      const response = await request({
        method: "GET",
        url: `${API.ADMIN_SHARED_CONFIGS}`,
        params,
        useLoader: true,
      });
      setSharedConfigs(response);
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch shared configs");
    }
  };

  const handleCreateEditSharedConfig = async (
    config: SharedConfigFormData,
    isEditMode: boolean,
    callback?: () => void,
  ) => {
    const body = {
      name: config.name ?? "",
      featureId: featureId ?? "",
      isEnabled: config.isEnabled ?? true,
      configuration:
        typeof config.configuration === "string"
          ? JSON.parse(config.configuration)
          : config.configuration ?? "",
    };
    const updateActionBody = {
      ...body,
      id: config.id,
      rowVersion: selectedEntity?.rowVersion,
    };
    await request({
      method: isEditMode ? "PUT" : "POST",
      url: API.ADMIN_SHARED_CONFIGS,
      useLoader: true,
      body: isEditMode ? updateActionBody : body,
      successMessage: isEditMode
        ? "Shared Config has been updated"
        : "Shared Config has been created",
      errorMessage: "Something went wrong!",
    });
    await fetchSharedConfigs();
    if (callback) callback();
    toggleModal({ hideAll: true });
  };

  const handleDeleteItem = async () => {
    await request({
      method: "DELETE",
      url: API.ADMIN_SHARED_CONFIGS,
      useLoader: true,
      body: {
        id: deleteEntity?.id,
        rowVersion: deleteEntity?.rowVersion,
      },
      successMessage: "Shared config deleted successfully",
      errorMessage: "Something went wrong!",
    });
    await fetchSharedConfigs();
    toggleModal({ key: "warning", value: true, hideAll: true });
  };

  const columns = useSharedConfigsColumns({
    onEdit: config => {
      toggleModal({
        key: "addEditConfig",
        value: true,
        selectedItem: {
          ...config,
        },
      });
    },
    onActivate: config => {
      toggleModal({
        key: "toggleEnabled",
        value: true,
        selectedItem: {
          ...config,
        },
      });
    },
    onDelete: (id, rowVersion) => {
      toggleModal({ key: "deleteConfig", value: true });
      setDeleteEntity({ id, rowVersion });
    },
  });

  useEffect(() => {
    if (!featureId) return;
    setFeature(availableFeatures.find((f: IAvailableFeatureData) => f.featureId === featureId));
    fetchSharedConfigs();
  }, [featureId, availableFeatures]);

  if (!feature) {
    return <div>Loading...</div>;
  }

  return (
    <div className="row">
      <PageTitle pageTitle="Feature Details" shouldBackByDelta isBack />

      <p>{feature.name}</p>
      <div className="col-12">
        <ul className="nav nav-tabs mb-4">
          <TabItem value="details" label="Details" activeTab={activeTab} onChange={setActiveTab} />
          <TabItem
            value="sharedConfigs"
            label="Shared Configs"
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        </ul>

        <Tab.Content>
          <Tab.Pane active={activeTab === "details"}>
            <div className="p-3 border rounded shadow-sm bg-white">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">Feature Information</h5>
              </div>

              <div className="row gy-3">
                <div className="col-5 col-lg-2">
                  <p className="fw-bold mb-0">Name:</p>
                </div>
                <div className="col-7 col-lg-10 text-lg-start text-end">
                  <p className="mb-0">{feature.name}</p>
                </div>

                <div className="col-5 col-lg-2">
                  <p className="fw-bold mb-0">Schema:</p>
                </div>
                <div className="col-7 col-lg-10 text-lg-start text-end">
                  <JsonEditor
                    data={feature.configurationSchema}
                    showCollectionCount={false}
                    enableClipboard={false}
                    rootName=""
                    viewOnly
                  />
                </div>
              </div>
            </div>
          </Tab.Pane>
          <Tab.Pane active={activeTab === "sharedConfigs"} className="d-flex flex-column gap-3">
            <WarningModal
              showModal={modals.deleteConfig}
              onCancel={() => {
                toggleModal({ hideAll: true });
              }}
              onClose={() => {
                toggleModal({ hideAll: true });
              }}
              modalTitle="Are you sure you want to delete this shared config ?"
              onSave={() => handleDeleteItem()}
            />
            <WarningModal
              showModal={modals.toggleEnabled}
              onCancel={() => {
                toggleModal({ hideAll: true });
              }}
              onClose={() => {
                toggleModal({ hideAll: true });
              }}
              modalTitle={`Are you sure that you want to ${
                selectedEntity?.isEnabled ? "deactivate" : "activate"
              } shared config ${selectedEntity?.name}?`}
              onSave={() =>
                handleCreateEditSharedConfig(
                  {
                    id: selectedEntity?.id,
                    name: selectedEntity?.name ?? "",
                    configuration: selectedEntity?.configuration,
                    isEnabled: !selectedEntity?.isEnabled,
                  },
                  true,
                )
              }
            />
            <CreateEditSharedConfigModal
              selectedEntity={selectedEntity}
              defaultValues={
                !selectedEntity?.configuration && {
                  configuration: generateTemplateFromSchema(feature.configurationSchema),
                }
              }
              showModal={modals.addEditConfig}
              onCancel={() => toggleModal({ hideAll: true })}
              onClose={() => toggleModal({ hideAll: true })}
              onSave={(modalData, isEditMode, callback) =>
                handleCreateEditSharedConfig(modalData!, isEditMode!, callback)
              }
            />
            <button
              type="button"
              className="btn btn-sm btn-success ms-auto"
              title="Create Shared Config"
              onClick={() => {
                toggleModal({ key: "addEditConfig", value: true });
              }}
            >
              Add Shared Config
            </button>
            <DataTable
              tableColumns={columns}
              tableData={sharedConfigs}
              tableClassName="table-striped mb-0"
              classContainer="table-responsive-xl card shadow-lg card-body"
            />
          </Tab.Pane>
        </Tab.Content>
      </div>
    </div>
  );
};

export default FeatureDetailsScreen;
